import { useState, useMemo, Fragment } from 'react';
import { useSVGBlurEvent } from '../../hooks/useSVGBlurEvent.js';
import { shouldHideAnnotation } from '../../utilities/shouldHideAnnotation.js';
import { isShowMoreAnnotationsButtonVisible } from '../../utilities/isShowMoreAnnotationsButtonVisible.js';
import { useAnnotationPositions } from './hooks/useAnnotationPositions.js';
import { SHOW_MORE_BUTTON_OFFSET, PILL_HEIGHT } from './constants.js';
import styles from './Annotations.scss.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { ShowMoreAnnotationsButton } from './components/ShowMoreAnnotationsButton/ShowMoreAnnotationsButton.js';
import { AnnotationLine } from './components/AnnotationLine/AnnotationLine.js';
import { AnnotationLabel } from './components/AnnotationLabel/AnnotationLabel.js';
import { AnnotationContent } from './components/AnnotationContent/AnnotationContent.js';

function Annotations({
  annotationsLookupTable,
  axisLabelWidth,
  drawableHeight,
  drawableWidth,
  labels,
  onHeightChange,
  xScale
}) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isShowingAllAnnotations, setIsShowingAllAnnotations] = useState(false);
  const [ref, setRef] = useState(null);
  const {
    annotations,
    dataIndexes
  } = useMemo(() => {
    const dataIndexes = {};
    const annotations = Object.keys(annotationsLookupTable).map(key => {
      const annotation = annotationsLookupTable[key];

      if (!labels.includes(key) || annotation == null || annotation.axis === 'y') {
        return null;
      }

      dataIndexes[key] = labels.indexOf(key);
      return annotation;
    }).filter(Boolean);
    return {
      annotations,
      dataIndexes
    };
  }, [annotationsLookupTable, labels]);
  const {
    hiddenAnnotationsCount,
    positions,
    rowCount
  } = useAnnotationPositions({
    annotations,
    axisLabelWidth,
    dataIndexes,
    drawableWidth,
    isShowingAllAnnotations,
    onHeightChange,
    xScale
  });

  const handleToggleAllAnnotations = () => {
    setIsShowingAllAnnotations(!isShowingAllAnnotations);
  };

  const handleOnMouseLeave = () => {
    setActiveIndex(-1);
  };

  useSVGBlurEvent({
    ref,
    onBlur: handleOnMouseLeave,
    checkFn: activeElement => {
      const focusedParent = activeElement === null || activeElement === void 0 ? void 0 : activeElement.parentElement;
      return (focusedParent === null || focusedParent === void 0 ? void 0 : focusedParent.dataset.isAnnotationContent) !== 'true';
    }
  });
  const isShowMoreButtonVisible = isShowMoreAnnotationsButtonVisible(rowCount);
  const showMoreButtonOffset = isShowMoreButtonVisible ? SHOW_MORE_BUTTON_OFFSET : 0;
  return /*#__PURE__*/jsxs("g", {
    ref: setRef,
    tabIndex: -1,
    className: styles.Group,
    children: [isShowMoreButtonVisible && /*#__PURE__*/jsx(ShowMoreAnnotationsButton, {
      annotationsCount: hiddenAnnotationsCount,
      collapseText: annotations[0].collapseButtonText,
      expandText: annotations[0].expandButtonText,
      isShowingAllAnnotations: isShowingAllAnnotations,
      onClick: handleToggleAllAnnotations,
      tabIndex: annotations.length,
      width: drawableWidth
    }), /*#__PURE__*/jsx("g", {
      transform: `translate(0, ${showMoreButtonOffset})`,
      children: positions.map(position => {
        const {
          line,
          y,
          row,
          index
        } = position;
        const annotation = annotations[index];

        if (shouldHideAnnotation({
          row,
          isShowingAllAnnotations,
          rowCount
        })) {
          return null;
        }

        const hasContent = annotation.content != null;
        const isContentVisible = index === activeIndex && hasContent;
        const tabIndex = index + 1;
        const ariaLabel = `${annotation.startKey}`;
        return /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(AnnotationLine, {
            size: drawableHeight - showMoreButtonOffset,
            x: line.x,
            y: y + PILL_HEIGHT
          }), /*#__PURE__*/jsx(AnnotationLabel, {
            ariaLabel: ariaLabel,
            hasContent: hasContent,
            index: index,
            isVisible: !isContentVisible,
            label: annotation.label,
            position: position,
            setActiveIndex: setActiveIndex,
            tabIndex: tabIndex
          }), isContentVisible && /*#__PURE__*/jsx(AnnotationContent, {
            annotation: annotation,
            drawableWidth: drawableWidth,
            index: index,
            onMouseLeave: handleOnMouseLeave,
            parentRef: ref,
            position: position,
            tabIndex: tabIndex,
            x: line.x,
            y: y
          })]
        }, `annotation${index}${annotation.startKey}`);
      })
    })]
  });
}

export { Annotations };
